import React, { useState, useEffect } from 'react';
import { Settings, Calendar, Clock, Shield } from 'lucide-react';
import GoogleCalendarIntegration from '../components/calendar/GoogleCalendarIntegration';
import OutlookCalendarIntegration from '../components/calendar/OutlookCalendarIntegration';
import { userPreferencesService, taskTypeDefaultsService } from '../services/api';
import useNotification from '../hooks/useNotification';
import Notification from '../components/common/Notification';

const CalendarSettings = () => {
  const { notification, showSuccess, showError, hideNotification } = useNotification();
  const [settings, setSettings] = useState({
    auto_sync_enabled: true,
    sync_interval_minutes: 15,
    conflict_resolution_preference: 'prompt',
    default_timezone: 'UTC'
  });
  const [eventTypeDefaults, setEventTypeDefaults] = useState([
    { type: 'court_date', name: 'Court Date', color: '#dc2626', duration: 120, prep: 60, travel: 30 },
    { type: 'meeting', name: 'Client Meeting', color: '#7c3aed', duration: 60, prep: 15, travel: 0 },
    { type: 'appointment', name: 'Appointment', color: '#2563eb', duration: 30, prep: 10, travel: 0 },
    { type: 'deadline', name: 'Deadline', color: '#eab308', duration: 60, prep: 0, travel: 0 },
    { type: 'task', name: 'Work Task', color: '#059669', duration: 60, prep: 0, travel: 0 },
    { type: 'call', name: 'Phone Call', color: '#06b6d4', duration: 30, prep: 5, travel: 0 }
  ]);
  const [privacySettings, setPrivacySettings] = useState({
    use_generic_titles: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Load calendar settings from API - no fallback
      const calendarResponse = await userPreferencesService.getCalendarSettings();
      const data = calendarResponse.data;

      if (data.calendar_settings) {
        setSettings(data.calendar_settings);
      }
      if (data.privacy_settings) {
        setPrivacySettings(data.privacy_settings);
      }

      // Load event type defaults from task type defaults API - no fallback
      const eventTypesResponse = await taskTypeDefaultsService.getTaskTypeDefaults();
      if (eventTypesResponse.data?.length > 0) {
        // Convert task type defaults to event type format
        const convertedEventTypes = eventTypesResponse.data.map(taskDefault => ({
          type: taskDefault.task_type,
          name: taskDefault.display_name || taskDefault.task_type.replace('_', ' '),
          color: taskDefault.color || '#2563eb',
          duration: taskDefault.estimated_duration_minutes || 60,
          prep: taskDefault.prep_time_minutes || 0,
          travel: taskDefault.travel_time_minutes || 0
        }));
        setEventTypeDefaults(convertedEventTypes);
      }

      setLoading(false);
    } catch (error) {
      console.error('Failed to load calendar settings from API:', error);
      setLoading(false);
      // Let the error propagate - don't hide missing API endpoints
      throw error;
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Save calendar settings to API - no fallback
      const calendarData = {
        calendar_settings: settings,
        privacy_settings: privacySettings
      };
      await userPreferencesService.updateCalendarSettings(calendarData);

      // Save event type defaults to task type defaults API - no fallback
      const taskTypeDefaults = eventTypeDefaults.map(eventType => ({
        task_type: eventType.type,
        display_name: eventType.name,
        color: eventType.color,
        estimated_duration_minutes: eventType.duration,
        prep_time_minutes: eventType.prep,
        travel_time_minutes: eventType.travel
      }));

      await taskTypeDefaultsService.bulkUpdateUserDefaults(taskTypeDefaults);

      showSuccess('Calendar settings saved successfully!');
    } catch (error) {
      console.error('Failed to save calendar settings to API:', error);
      let errorMessage = 'Failed to save settings. API endpoint may be missing.';

      if (error.response?.status === 404) {
        errorMessage = 'Settings API endpoint not implemented. Please add the /users/preferences/calendar endpoint.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error while saving settings. Check backend logs.';
      } else if (error.response?.data?.detail) {
        errorMessage = `API Error: ${error.response.data.detail}`;
      }

      showError(errorMessage);
      // Don't hide the error - let it be visible in the console
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const updateEventTypeDefault = (index, field, value) => {
    const updatedEventTypes = [...eventTypeDefaults];
    updatedEventTypes[index] = {
      ...updatedEventTypes[index],
      [field]: parseInt(value) || 0
    };
    setEventTypeDefaults(updatedEventTypes);
  };

  const handleGoogleCalendarStatusChange = (status) => {
    console.log('Google Calendar status changed:', status);
  };

  const handleOutlookCalendarStatusChange = (status) => {
    console.log('Outlook Calendar status changed:', status);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-8">
      <div className="flex items-center space-x-3 mb-6">
        <Settings className="h-8 w-8 text-yellow-600" />
        <h1 className="text-3xl font-bold text-gray-900">Calendar Settings</h1>
      </div>

      {/* Google Calendar Integration */}
      <GoogleCalendarIntegration 
        onStatusChange={handleGoogleCalendarStatusChange}
      />

      {/* Outlook Calendar Integration */}
      <OutlookCalendarIntegration
        onStatusChange={handleOutlookCalendarStatusChange}
      />

      {/* General Settings */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Clock className="h-6 w-6 text-gray-600" />
            <h3 className="text-lg font-semibold">Sync Settings</h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Auto-sync enabled
                </label>
                <p className="text-sm text-gray-500">
                  Automatically sync events with external calendars
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.auto_sync_enabled}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    auto_sync_enabled: e.target.checked
                  }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sync interval
              </label>
              <select
                value={settings.sync_interval_minutes}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  sync_interval_minutes: parseInt(e.target.value)
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value={5}>Every 5 minutes</option>
                <option value={15}>Every 15 minutes</option>
                <option value={30}>Every 30 minutes</option>
                <option value={60}>Every hour</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conflict resolution
              </label>
              <select
                value={settings.conflict_resolution_preference}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  conflict_resolution_preference: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="prompt">Ask me what to do</option>
                <option value="auto_reschedule">Auto-reschedule conflicts</option>
                <option value="ignore">Ignore conflicts</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default timezone
              </label>
              <select
                value={settings.default_timezone}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  default_timezone: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Event Type Defaults */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Calendar className="h-6 w-6 text-gray-600" />
            <h3 className="text-lg font-semibold">Event Type Defaults</h3>
          </div>

          <div className="space-y-4">
            {eventTypeDefaults.map((eventType, index) => (
              <div key={eventType.type} className="border border-gray-200 rounded-md p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: eventType.color }}
                  ></div>
                  <h4 className="font-medium">{eventType.name}</h4>
                </div>

                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <label className="block text-gray-600 mb-1">Duration (min)</label>
                    <input
                      type="number"
                      value={eventType.duration}
                      onChange={(e) => updateEventTypeDefault(index, 'duration', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 mb-1">Prep time (min)</label>
                    <input
                      type="number"
                      value={eventType.prep}
                      onChange={(e) => updateEventTypeDefault(index, 'prep', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-600 mb-1">Travel time (min)</label>
                    <input
                      type="number"
                      value={eventType.travel}
                      onChange={(e) => updateEventTypeDefault(index, 'travel', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-center"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Privacy Settings */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="h-6 w-6 text-gray-600" />
            <h3 className="text-lg font-semibold">Privacy Settings</h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Use generic titles in external calendars
                </label>
                <p className="text-sm text-gray-500">
                  Show "Client Meeting" instead of client names for privacy
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={privacySettings.use_generic_titles}
                  onChange={(e) => setPrivacySettings(prev => ({
                    ...prev,
                    use_generic_titles: e.target.checked
                  }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="px-6 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:opacity-50 flex items-center space-x-2"
        >
          {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
          <span>{saving ? 'Saving...' : 'Save Settings'}</span>
        </button>
      </div>

      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default CalendarSettings;
